/**
 * Core Web Vitals Tracker
 * Monitorea y reporta métricas de performance críticas para SEO
 */

import { getCLS, getFCP, getFID, getLCP, getTTFB } from "web-vitals";
import { trackCoreWebVitals } from "../components/Analytics/GoogleAnalytics";
import { gtmTrackCoreWebVitals } from "../components/Analytics/GoogleTagManager";

// Thresholds de Core Web Vitals (Google)
const VITALS_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 },
  FID: { good: 100, needsImprovement: 300 },
  CLS: { good: 0.1, needsImprovement: 0.25 },
  FCP: { good: 1800, needsImprovement: 3000 },
  TTFB: { good: 800, needsImprovement: 1800 },
};

// Función para clasificar métricas
const getMetricRating = (value, thresholds) => {
  if (!thresholds) return "unknown";
  if (value <= thresholds.good) return "good";
  if (value <= thresholds.needsImprovement) return "needs-improvement";
  return "poor";
};

// Función para enviar métricas a analytics
const sendToAnalytics = ({ name, delta, value, id }) => {
  // Normalizar el nombre de la métrica
  const metricName = name.toUpperCase();
  const thresholds = VITALS_THRESHOLDS[metricName];
  const rating = getMetricRating(value, thresholds);

  console.log(`[Core Web Vitals] ${name}: ${value} (${rating})`);

  // Enviar a Google Analytics
  if (typeof trackCoreWebVitals === "function") {
    trackCoreWebVitals({ name, delta, value, id, rating });
  }

  // Enviar a Google Tag Manager
  if (typeof gtmTrackCoreWebVitals === "function") {
    gtmTrackCoreWebVitals({ name, delta, value, id, rating });
  }

  // Log para desarrollo
  if (process.env.NODE_ENV === "development") {
    console.log(`Core Web Vital - ${name}:`, {
      value: Math.round(value),
      rating,
      delta: Math.round(delta),
      id,
    });
  }

  // Enviar a servicio de monitoreo (opcional)
  if (window.TRINEO_ANALYTICS_ENDPOINT) {
    fetch(window.TRINEO_ANALYTICS_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        metric: name,
        value: Math.round(value),
        rating,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      }),
    }).catch(console.error);
  }
};

// Inicializar tracking de Core Web Vitals
export const initCoreWebVitals = () => {
  try {
    // Métricas principales de Core Web Vitals
    getCLS(sendToAnalytics, { reportAllChanges: false });
    getFID(sendToAnalytics);
    getLCP(sendToAnalytics, { reportAllChanges: false });

    // Métricas adicionales importantes para performance
    getFCP(sendToAnalytics);
    getTTFB(sendToAnalytics);

    // Métricas personalizadas de Trineo
    measureCustomMetrics();
  } catch (error) {
    console.error("Error initializing Core Web Vitals:", error);
  }
};

// Métricas personalizadas específicas de Trineo
const measureCustomMetrics = () => {
  // Time to Interactive
  if ("requestIdleCallback" in window) {
    requestIdleCallback(() => {
      const navigationEntry = performance.getEntriesByType("navigation")[0];
      if (navigationEntry) {
        const tti = navigationEntry.loadEventEnd - navigationEntry.fetchStart;
        sendToAnalytics({
          name: "TTI",
          value: tti,
          delta: tti,
          id: "custom-tti",
        });
      }
    });
  }

  // Hero Section Load Time
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.name.includes("hero") || entry.name.includes("HeroSection")) {
        sendToAnalytics({
          name: "HERO_LOAD",
          value: entry.startTime,
          delta: entry.startTime,
          id: "hero-load-time",
        });
      }
    }
  });

  if ("PerformanceObserver" in window) {
    observer.observe({ entryTypes: ["measure"] });
  }

  // Bundle Load Time
  window.addEventListener("load", () => {
    const resources = performance.getEntriesByType("resource");
    const jsResources = resources.filter(
      (resource) =>
        resource.name.includes(".js") && resource.name.includes("static")
    );

    if (jsResources.length > 0) {
      const totalBundleTime = Math.max(
        ...jsResources.map((r) => r.responseEnd)
      );
      sendToAnalytics({
        name: "BUNDLE_LOAD",
        value: totalBundleTime,
        delta: totalBundleTime,
        id: "bundle-load-time",
      });
    }
  });
};

// Función para reportar errores de performance
export const reportPerformanceIssue = (issue, details = {}) => {
  if (typeof trackCoreWebVitals === "function") {
    trackCoreWebVitals(
      "PERFORMANCE_ISSUE",
      JSON.stringify({ issue, ...details }),
      "poor"
    );
  }

  console.warn("Performance Issue:", issue, details);
};

// Hook para componentes React
export const usePerformanceMonitoring = (componentName) => {
  const startTime = performance.now();

  return {
    markRenderComplete: () => {
      const renderTime = performance.now() - startTime;
      sendToAnalytics({
        name: "COMPONENT_RENDER",
        value: renderTime,
        delta: renderTime,
        id: `${componentName}-render`,
      });
    },
  };
};

export default initCoreWebVitals;
